import { api } from "@/api/client";

// Close Review: month-end checks, P&L, Balance Sheet and Statistical Journals
// pushed by fsp-bi-hub, with accounting's review and comments. Mirrors
// app/serializers/close_review_serializer.rb.

export type CheckStatus = "pass" | "flag" | "info" | "error";
export type ReviewStatus = "open" | "acknowledged" | "resolved" | "waived";
export type ReportKey = "pl" | "bs" | "stat_journals";
export type ColumnType = "text" | "money" | "number" | "percent" | "date" | "link";

export interface Column { key: string; label: string; type: ColumnType }

export interface Schedule {
  first_checks_at: string;
  stat_journals_at: string;
  last_checks_at: string;
  notes: Record<"checks" | ReportKey, string>;
}

export interface PeriodSummary {
  month: string;
  label: string;
  loaded: boolean;
  latest_run_at: string | null;
  business_day: number | null;
  links: { package?: string };
  status_counts: Partial<Record<CheckStatus, number>>;
  open_flags: number;
  changed_since_review: number;
  schedule: Schedule;
}

export interface CheckSummary {
  key: string;
  group: string;
  title: string;
  account: string | null;
  basis: "period_end" | "run_date";
  status: CheckStatus;
  summary: string;
  expected: number | null;
  actual: number | null;
  difference: number | null;
  threshold: number | null;
  last_run_at: string;
  review_status: ReviewStatus;
  review_note: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  changed_since_review: boolean;
  comment_count: number;
  open_comment_count: number;
}

export interface ReportRow { key: string; style: "section" | "data" | "total" | "ratio"; format?: ColumnType; values: Record<string, unknown> }
export interface Report {
  key: ReportKey;
  title: string;
  basis: string | null;
  status: "preliminary" | "final" | "error";
  note: string | null;
  columns: Column[];
  rows: ReportRow[];
  run_at: string;
}

export interface CheckRow { id: number; key: string; values: Record<string, unknown>; cleared: boolean; cleared_at: string | null; first_seen_at: string; comment_count: number }
export interface ReviewComment {
  id: number;
  row_id: number | null;
  row_key: string | null;
  body: string;
  author: string;
  mine: boolean;
  can_delete: boolean;
  created_at: string;
  resolved: boolean;
  resolved_at: string | null;
  resolved_by: string | null;
}
export interface HistoryEntry { run_at: string; business_day: number | null; status: CheckStatus; expected: number | null; actual: number | null; difference: number | null }
export interface CheckDetail extends CheckSummary {
  columns: Column[];
  rows: CheckRow[];
  history: HistoryEntry[];
  comments: ReviewComment[];
}

const base = "/close_review/periods";
const checkPath = (month: string, key: string) => `${base}/${month}/checks/${encodeURIComponent(key)}`;

export async function fetchPeriods(): Promise<PeriodSummary[]> {
  const { data } = await api.get<{ periods: PeriodSummary[] }>(base);
  return data.periods;
}

export interface StatJournalLine { account: string; statistic: string; class: string; location: string; department: string; quantity: number }
export interface StatJournal {
  id: number;
  external_id: string;
  label: string;
  group: string | null;
  status: "pending" | "approved" | "posted" | "rejected";
  decided_by: string | null;
  decided_at: string | null;
  comment: string | null;
  netsuite_url: string | null;
  evidence_url: string | null;
  lines: StatJournalLine[];
}

export interface PeriodPage {
  period: PeriodSummary;
  checks: CheckSummary[];
  reports: Report[];
  stat_journals: StatJournal[];
  can_approve_stat_journals: boolean;
}

export async function fetchPeriod(month: string): Promise<PeriodPage> {
  const { data } = await api.get(`${base}/${month}`);
  return data;
}

// Approve (posts it to NetSuite) or reject a statistical journal; fsp-bi-hub does the deciding and posting.
export async function decideStatJournal(month: string, id: number, choice: "approve" | "reject", comment: string): Promise<StatJournal> {
  const { data } = await api.post<{ stat_journal: StatJournal }>(`${base}/${month}/stat_journals/${id}/decision`, { choice, comment });
  return data.stat_journal;
}

export async function fetchCheck(month: string, key: string): Promise<{ period: PeriodSummary; check: CheckDetail }> {
  const { data } = await api.get(checkPath(month, key));
  return data;
}

export async function reviewCheck(month: string, key: string, review_status: ReviewStatus, note?: string): Promise<CheckDetail> {
  const { data } = await api.post<{ check: CheckDetail }>(`${checkPath(month, key)}/review`, { review_status, note });
  return data.check;
}

export async function createComment(month: string, key: string, body: string, rowId?: number | null): Promise<ReviewComment> {
  const { data } = await api.post<ReviewComment>(`${checkPath(month, key)}/comments`, { body, row_id: rowId ?? undefined });
  return data;
}

export async function setCommentResolved(id: number, resolved: boolean): Promise<ReviewComment> {
  const { data } = await api.post<ReviewComment>(`/close_review_comments/${id}/${resolved ? "resolve" : "reopen"}`);
  return data;
}

export async function deleteComment(id: number): Promise<void> {
  await api.delete(`/close_review_comments/${id}`);
}

export function errorMessage(error: unknown): string {
  const data = (error as { response?: { data?: { error?: string; errors?: string[] } } })?.response?.data;
  return data?.error ?? data?.errors?.join(", ") ?? (error as Error)?.message ?? "Something went wrong";
}
